import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, ArrowRight, Filter } from 'lucide-react';
import SEO from '../components/SEO';
import GradientOverlay from '../components/GradientOverlay';
import LogomarkPattern from '../components/LogomarkPattern';
import CTASection from '../components/CTASection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper component for blog images with fallback
const BlogImage = ({ src, alt, style }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const imageUrl = src && src.startsWith('/') ? `${API_URL}${src}` : src;
  
  if (!src || hasError) {
    return (
      <div style={{
        ...style,
        backgroundColor: '#e8f4f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.08} density="medium" tint="teal" />
      </div>
    );
  }
  
  return (
    <div style={{
      ...style,
      backgroundColor: '#e8f4f5',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {isLoading && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <LogomarkPattern opacity={0.08} density="medium" tint="teal" />
        </div>
      )}
      <img
        src={imageUrl}
        alt={alt}
        onError={() => setHasError(true)}
        onLoad={() => setIsLoading(false)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.3s ease'
        }}
      />
    </div>
  );
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');

  useEffect(() => {
    fetchCategories();
    fetchPosts();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, sortOrder]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/blog/posts?sort=${sortOrder}`;
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPostsWithSearch();
  };

  const fetchPostsWithSearch = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/blog/posts?sort=${sortOrder}`;
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredPosts = posts;

  return (
    <div style={{ marginTop: '80px' }}>
      <SEO 
        title="Blog - Insights & Resources"
        description="Expert insights on CRM, ERP, AI enablement, and digital transformation for Australian SMEs. Stay ahead with Resolv Digital's latest articles and resources."
        keywords="Digital Transformation Blog, CRM Tips Australia, ERP Insights, AI Business Blog, SME Technology Blog, Gold Coast Business Insights"
        canonical="https://resolvdigital.com.au/blog"
      />
      
      {/* Hero Section - LIGHT */}
      <section style={{
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#ffffff',
        padding: '120px clamp(20px, 5vw, 40px) 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <GradientOverlay variant="subtle" opacity={0.15} />
        <LogomarkPattern opacity={0.03} density="sparse" tint="teal" />
        
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '8%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(13, 115, 119, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />

        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h1 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 6vw, 5rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #0D7377 50%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '24px'
          }}>
            Insights & Resources
          </h1>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#0a4447',
            maxWidth: '700px'
          }}>
            Expert perspectives on digital transformation, CRM optimisation, and technology strategy for Australian businesses.
          </p>
        </div>
      </section>

      {/* Filters Section */}
      <section style={{
        padding: '40px clamp(20px, 5vw, 40px)',
        backgroundColor: '#e8f4f5',
        borderBottom: '1px solid rgba(13, 115, 119, 0.1)'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '20px',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            {/* Search */}
            <form onSubmit={handleSearch} style={{
              display: 'flex',
              gap: '12px',
              flex: '1 1 300px',
              maxWidth: '500px'
            }}>
              <div style={{
                flex: 1,
                position: 'relative'
              }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#0D7377'
                }} />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px 16px 14px 48px',
                    borderRadius: '8px',
                    border: '2px solid rgba(13, 115, 119, 0.2)',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    backgroundColor: '#ffffff'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0D7377'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(13, 115, 119, 0.2)'}
                  data-testid="blog-search-input"
                />
              </div>
              <button
                type="submit"
                style={{
                  padding: '14px 24px',
                  backgroundColor: '#0D7377',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontFamily: 'Lato, sans-serif',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#0a5d60'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#0D7377'}
                data-testid="blog-search-button"
              >
                Search
              </button>
            </form>

            {/* Filters */}
            <div style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Filter size={18} style={{ color: '#0D7377' }} />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid rgba(13, 115, 119, 0.2)',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.95rem',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                    outline: 'none',
                    minWidth: '160px'
                  }}
                  data-testid="blog-category-filter"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Calendar size={18} style={{ color: '#0D7377' }} />
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid rgba(13, 115, 119, 0.2)',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.95rem',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                    outline: 'none',
                    minWidth: '140px'
                  }}
                  data-testid="blog-sort-filter"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section style={{
        padding: '80px clamp(20px, 5vw, 40px)',
        backgroundColor: '#ffffff',
        minHeight: '50vh'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '80px 20px'
            }}>
              <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                color: '#0a4447'
              }}>Loading articles...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '80px 20px'
            }}>
              <h2 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#0a4447',
                marginBottom: '16px'
              }}>No articles found</h2>
              <p style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                color: '#4a5568'
              }}>
                {searchQuery || selectedCategory 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Check back soon for new content!'}
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))',
              gap: '40px'
            }}>
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid rgba(13, 115, 119, 0.15)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(13, 115, 119, 0.15)';
                    e.currentTarget.style.borderColor = '#0D7377';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = 'rgba(13, 115, 119, 0.15)';
                  }}
                  data-testid={`blog-post-card-${post.slug}`}
                >
                  {/* Banner Image */}
                  <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                    <BlogImage 
                      src={post.banner_image} 
                      alt={post.title}
                      style={{
                        height: '200px',
                        width: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Link>

                  {/* Content */}
                  <div style={{
                    padding: '28px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1
                  }}>
                    {/* Category & Date */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '16px',
                      flexWrap: 'wrap'
                    }}>
                      {post.category_name && (
                        <span style={{
                          backgroundColor: 'rgba(13, 115, 119, 0.1)',
                          color: '#0D7377',
                          padding: '4px 12px',
                          borderRadius: '4px',
                          fontFamily: 'Lato, sans-serif',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {post.category_name}
                        </span>
                      )}
                      <span style={{
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.875rem',
                        color: '#6b7280'
                      }}>
                        {formatDate(post.created_at)}
                      </span>
                    </div>

                    {/* Title */}
                    <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                      <h2 style={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.375rem',
                        lineHeight: 1.3,
                        color: '#0a4447',
                        marginBottom: '12px',
                        transition: 'color 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#0D7377'}
                      onMouseLeave={(e) => e.target.style.color = '#0a4447'}
                      >
                        {post.title}
                      </h2>
                    </Link>

                    {/* Excerpt */}
                    <p style={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '1rem',
                      lineHeight: 1.6,
                      color: '#4a5568',
                      marginBottom: '20px',
                      flex: 1
                    }}>
                      {post.excerpt}
                    </p>

                    {/* Read More */}
                    <Link
                      to={`/blog/${post.slug}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: '#0D7377',
                        textDecoration: 'none',
                        transition: 'gap 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.gap = '12px'}
                      onMouseLeave={(e) => e.currentTarget.style.gap = '8px'}
                    >
                      Read Article <ArrowRight size={18} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <CTASection />
    </div>
  );
};

export default Blog;
